import { Field, Form, Formik }  from 'formik';
import { useCallback, useMemo } from 'react';
import useSWR                   from 'swr';

import { areFieldsMatched, handleCheckMatchedFields, queryParams, splitCamelCase } from '@shared/commonFunctions';
import { SERVICES }                                                                from '@shared/constants/apiConfig';
import { ModalFooter }                                                             from '@styles/ModalStyles';

import { Modal } from '@shared/components/modals/Modal';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { SingleRadioButton }                                                           from '../SingleRadioButton/SingleRadioButton';
import { DataTable }                                                                   from '../Table/DataTable';
import { TDataTableRow }                                                               from '../../interfaces';
import { requestUpdateOCRData }                                                        from '../../queries';
import { CORRECT_OCR_FIELDS_ORDER, IOCRFormValues, IOCRModalProps, OCR_TABLE_HEADERS } from './interfaces';

export const OCRModal = ({ ocrData, loading, mutateOcrData, handleClose, ocrModalPosition }: IOCRModalProps) => {
  const params = queryParams();

  const { mutate: reloadContact } = useSWR(params.id && `/${SERVICES.CONTACT}/contacts/${params.id}`);

  const sortedData = useMemo(() => {
    const getFieldCorrectLabel = (fieldName: string) => CORRECT_OCR_FIELDS_ORDER.findIndex((item) => item.value === fieldName);
      
    return (ocrData || []).sort((a, b) => getFieldCorrectLabel(a.fieldName) - getFieldCorrectLabel(b.fieldName));
  }, [ocrData]);

  const rows: TDataTableRow[] = useMemo(() =>
    sortedData.map((item) => {
      const matchedField = CORRECT_OCR_FIELDS_ORDER.find((field) => field.value === item.fieldName);
      const label        = matchedField ? matchedField.label : splitCamelCase(item.fieldName);

      const row = [
        { value: label },
        {
          value: areFieldsMatched(item.currentValue, item.ocrValue) ? (
            <div>{item.currentValue}</div>
          ) : (
            <Field
              component       = {SingleRadioButton}
              name            = {`currentValue.${item.fieldName}`}
              dependFieldName = {`ocrValue.${item.fieldName}`}
              label           = {item.currentValue}
            />
          ),
        },
        {
          value: areFieldsMatched(item.currentValue, item.ocrValue) ? (
            <div>{item.currentValue}</div>
          ) : (
            <Field
              component       = {SingleRadioButton}
              name            = {`ocrValue.${item.fieldName}`}
              dependFieldName = {`currentValue.${item.fieldName}`}
              label           = {item.ocrValue}
            />
          ),
        },
        { value: handleCheckMatchedFields(item.currentValue, item.ocrValue) },
      ];

      return row;
    }), [sortedData, areFieldsMatched],
  );

  const handleCorrectValues = useCallback((data: IOCRFormValues) => ocrData
    .filter((item) => Object.keys(data['currentValue']).includes(item.fieldName))
    .map((item) => ({
      contactId : item.contactId,
      assetId   : item.assetId,
      tableName : item.tableName,
      name      : item.fieldName,
      value     : data.currentValue[item.fieldName] ? item.currentValue : item.ocrValue,
    }),
  ), [ocrData]);

  const handleSubmit = useCallback(
    async(data: IOCRFormValues) => {
      const mappedValues = handleCorrectValues(data);

      await requestUpdateOCRData(mappedValues);
      handleClose();
      mutateOcrData();
      reloadContact();
    },
    [requestUpdateOCRData, ocrData, handleCorrectValues, mutateOcrData, handleClose, reloadContact],
  );

  return (
    <Modal
      open
      hideBackdrop
      marginTop     = "30px"
      title         = "OCR Review"
      dialogWidth   = "800px"
      resetPosition = {ocrModalPosition}
      onClose       = {handleClose}
    >
      <Formik
        initialValues    = {{ currentValue: {}, ocrValue: {} }}
        validationSchema = {null}
        onSubmit         = {handleSubmit}
      >
        {(props) => (
          <Form>
            <DataTable
              title        = {ocrData?.[0]?.tableName ?? ''}
              rows         = {rows}
              loading      = {loading}
              noResultText = "There is no OCR data to review"
              headers      = {OCR_TABLE_HEADERS}
            />

            <ModalFooter>
              <CancelButton onClick={handleClose} />
              <SaveButton label='Confirm' disabled={loading || !Object.keys(props.touched).length} />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
